/**
 * Promo cards injected into the marketplace hits grid.
 *
 * All the behaviour for the special "promo" card lives here so it can be
 * enabled/disabled from a single switch without touching the search wiring.
 *
 * ── Enable / disable ─────────────────────────────────────────
 * Flip `PROMO_CARDS_ENABLED` to false to remove the card entirely:
 * injection, template branch and the full-width `<li>` styling all become
 * no-ops, leaving the plain vehicle grid untouched.
 */
export const PROMO_CARDS_ENABLED = true;

/** 1-based slot where the promo card appears (5 = 5th tile). */
const PROMO_SLOT = 5;

/** Shape of the synthetic item injected into the hits list. */
interface PromoItem {
    __promo: true;
}

/** Type guard used by the `hits` item template. */
export function isPromoCard(hit: unknown): hit is PromoItem {
    return Boolean(hit && (hit as PromoItem).__promo);
}

/** Reassurance items shown in the inserted promo card (mirrors the comparateur trust row). */
const PROMO_ITEMS: { title: string; text: string }[] = [
    {
        title: "260+ points contrôlés",
        text: "Mécanique, carrosserie, électronique : chaque véhicule passe par notre expertise avant livraison.",
    },
    {
        title: "24 mois de garantie minimum",
        text: "Couverture mécanique, électrique et électronique. Pièces et main-d'œuvre incluses.",
    },
    {
        title: "Livré révisé, prêt à rouler",
        text: "Vidange et points d'usure traités en concession avant la remise des clés.",
    },
];

const PROMO_GUIDES_URL = "https://blog.joinsteer.com";

/** Inner HTML of the promo card (rendered inside a hits `<li>`). */
export function promoCardTemplate(): string {
    const rows = PROMO_ITEMS.map(
        (item, i) => `
            <li class="marketplace-promo-card__item">
                <span class="marketplace-promo-card__num">${String(i + 1).padStart(2, "0")}</span>
                <div class="marketplace-promo-card__item-content">
                    <p class="marketplace-promo-card__item-title">${item.title}</p>
                    <p class="marketplace-promo-card__item-text">${item.text}</p>
                </div>
            </li>
        `,
    ).join("");

    return `
        <div class="marketplace-promo-card" data-testid="marketplace-promo-card">
            <div class="marketplace-promo-card__head">
                <span class="marketplace-promo-card__eyebrow">Pourquoi Joinsteer</span>
                <span class="marketplace-promo-card__dot" aria-hidden="true"></span>
            </div>
            <p class="marketplace-promo-card__title">L'achat serein</p>
            <ol class="marketplace-promo-card__list">
                ${rows}
            </ol>
            <a class="marketplace-promo-card__footer" href="${PROMO_GUIDES_URL}" target="_blank" rel="noopener">
                <span>Nos guides d'achat</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17 17 7M9 7h8v8"/></svg>
            </a>
        </div>
    `;
}

/**
 * `transformItems` handler for the `hits` widget.
 *
 * Inserts the promo card at `PROMO_SLOT`, first page only, so positions stay
 * predictable and pagination/stats (driven by `results.nbHits`) are unaffected
 * — we only add a tile, we never remove a real hit.
 */
export function injectPromoCards<T>(
    items: T[],
    results: { page?: number } | null | undefined,
): T[] {
    if (!PROMO_CARDS_ENABLED) return items;
    if ((results?.page ?? 0) !== 0) return items;
    if (items.length < PROMO_SLOT) return items;

    const out = [...items];
    out.splice(PROMO_SLOT - 1, 0, { __promo: true } as unknown as T);
    return out;
}

/** Marks the first real vehicle hit so its card image is emitted eager in HTML. */
export function markMarketplaceLcpHit<T>(
    items: T[],
): (T & { __lcpImage?: true })[] {
    let marked = false;

    return items.map((item): T & { __lcpImage?: true } => {
        if (marked || isPromoCard(item)) {
            return item as T & { __lcpImage?: true };
        }

        marked = true;

        return { ...item, __lcpImage: true as const };
    });
}
